/**
 * Comprehensive Demo & Sample Data Seeder for Filing & Billing Modules
 * apps/gst-portal/src/services/seedDataService.ts
 */

import { collection, doc, writeBatch, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { sanitizeForFirestore } from '../utils/sanitizeFirestore';
import {
  ExtractedGstInvoice,
  Gstr2bRecord,
  MerchantInvoice,
  MerchantBillingSettings,
  GstProfile
} from '../types/gst';
import { calculateInvoiceTotals } from './billingService';

export const DEMO_GSTIN = '27AABCU9603R1ZM';
export const DEMO_COMPANY = 'Verve AI Labs Pvt Ltd';

export async function seedDemoDataForClient(clientId: string): Promise<{
  salesCount: number;
  purchaseCount: number;
  gstr2bCount: number;
  billingInvoiceCount: number;
}> {
  if (!clientId) throw new Error('Client ID is required to seed demo data.');

  const batch = writeBatch(db);
  const now = new Date().toISOString();
  const currentFy = '2025-26';
  const currentPeriod = 'July';

  // 1. Setup Master GST Profile
  const profileRef = doc(db, 'clients', clientId, 'gst_profile', DEMO_GSTIN);
  const profile: GstProfile = {
    gstin: DEMO_GSTIN,
    clientId,
    entityType: 'PVT_LTD',
    legalName: DEMO_COMPANY,
    tradeName: 'Verve AI Labs',
    pan: 'AABCU9603R',
    cinOrLlpin: 'U72900MH2024PTC428901',
    stateCode: '27',
    stateName: 'Maharashtra',
    filingFrequency: 'MONTHLY',
    isActive: true,
    createdAt: now,
    updatedAt: now
  };
  batch.set(profileRef, sanitizeForFirestore(profile), { merge: true });

  // 2. Setup Merchant Billing Settings
  const settingsRef = doc(db, 'clients', clientId, 'billing_settings', 'default');
  const settings: MerchantBillingSettings = {
    clientId,
    companyName: DEMO_COMPANY,
    tradeName: 'Verve AI Labs',
    gstin: DEMO_GSTIN,
    pan: 'AABCU9603R',
    phone: '+91 98200 99881',
    email: 'billing@verveailabs.com',
    address: 'Unit 402, Quantum Heights, BKC, Bandra East, Mumbai - 400051',
    stateCode: '27',
    stateName: 'Maharashtra',
    bankName: 'HDFC Bank Ltd',
    accountNumber: '50200088991122',
    ifscCode: 'HDFC0000240',
    branchName: 'BKC Branch, Mumbai',
    upiId: 'verveailabs@hdfcbank',
    defaultTerms: '1. Payment due within 15 days of invoice date.\n2. Interest @ 18% p.a. will be charged on overdue payments.\n3. Goods/Services once provided are non-refundable.\n4. Subject to Mumbai Jurisdiction.',
    invoicePrefix: 'VERVE/25-26/',
    nextInvoiceSequence: 105,
    defaultTemplateId: 'CLASSIC_GST',
    accentColor: '#06b6d4'
  };
  batch.set(settingsRef, sanitizeForFirestore(settings), { merge: true });

  // 3. Outward Sales Invoices
  const salesInvoices: ExtractedGstInvoice[] = [
    {
      id: `demo_sale_1_${clientId}`,
      clientId,
      financialYear: currentFy,
      taxPeriod: currentPeriod,
      category: 'B2B_SALES',
      supplier_gstin: DEMO_GSTIN,
      supplier_name: DEMO_COMPANY,
      buyer_gstin: '27AABCT2201M1ZT',
      invoice_number: 'VERVE/25-26/101',
      normalized_invoice_number: 'VERVE2526101',
      invoice_date: '2025-07-04',
      place_of_supply: '27-Maharashtra',
      reverse_charge_applicable: false,
      line_items: [
        {
          description: 'Enterprise AI Copilot Platform - Quarterly License',
          hsn_sac_code: '998313',
          qty: 1,
          unit_price: 150000,
          taxable_value: 150000,
          cgst_rate: 9,
          sgst_rate: 9,
          igst_rate: 0,
          cess_rate: 0,
          cgst_amount: 13500,
          sgst_amount: 13500,
          igst_amount: 0,
          cess_amount: 0
        }
      ],
      total_taxable_value: 150000,
      total_tax: 27000,
      invoice_total: 177000,
      math_verified: true,
      itc_eligibility: 'ELIGIBLE',
      recon_status: 'UNMATCHED',
      source_file_path: 'mock_sales_register.xlsx',
      created_at: now,
      audit_trail: [{ action: 'DEMO_SEEDED', timestamp: now, userId: 'system' }]
    },
    {
      id: `demo_sale_2_${clientId}`,
      clientId,
      financialYear: currentFy,
      taxPeriod: currentPeriod,
      category: 'B2B_SALES',
      supplier_gstin: DEMO_GSTIN,
      supplier_name: DEMO_COMPANY,
      buyer_gstin: '07AAACA1234F1Z8',
      invoice_number: 'VERVE/25-26/102',
      normalized_invoice_number: 'VERVE2526102',
      invoice_date: '2025-07-10',
      place_of_supply: '07-Delhi',
      reverse_charge_applicable: false,
      line_items: [
        {
          description: 'Custom LLM Fine-Tuning & Pipeline Architecture',
          hsn_sac_code: '998314',
          qty: 1,
          unit_price: 280000,
          taxable_value: 280000,
          cgst_rate: 0,
          sgst_rate: 0,
          igst_rate: 18,
          cess_rate: 0,
          cgst_amount: 0,
          sgst_amount: 0,
          igst_amount: 50400,
          cess_amount: 0
        }
      ],
      total_taxable_value: 280000,
      total_tax: 50400,
      invoice_total: 330400,
      math_verified: true,
      itc_eligibility: 'ELIGIBLE',
      recon_status: 'UNMATCHED',
      source_file_path: 'mock_sales_register.xlsx',
      created_at: now,
      audit_trail: [{ action: 'DEMO_SEEDED', timestamp: now, userId: 'system' }]
    },
    {
      id: `demo_sale_3_${clientId}`,
      clientId,
      financialYear: currentFy,
      taxPeriod: currentPeriod,
      category: 'B2C_RETAIL',
      supplier_gstin: DEMO_GSTIN,
      supplier_name: DEMO_COMPANY,
      buyer_gstin: '',
      invoice_number: 'VERVE/25-26/103',
      normalized_invoice_number: 'VERVE2526103',
      invoice_date: '2025-07-18',
      place_of_supply: '27-Maharashtra',
      reverse_charge_applicable: false,
      line_items: [
        {
          description: 'Developer AI API Credits Bundle (Pay-as-you-go)',
          hsn_sac_code: '998431',
          qty: 5,
          unit_price: 5000,
          taxable_value: 25000,
          cgst_rate: 9,
          sgst_rate: 9,
          igst_rate: 0,
          cess_rate: 0,
          cgst_amount: 2250,
          sgst_amount: 2250,
          igst_amount: 0,
          cess_amount: 0
        }
      ],
      total_taxable_value: 25000,
      total_tax: 4500,
      invoice_total: 29500,
      math_verified: true,
      itc_eligibility: 'ELIGIBLE',
      recon_status: 'UNMATCHED',
      source_file_path: 'mock_pos_register.csv',
      created_at: now,
      audit_trail: [{ action: 'DEMO_SEEDED', timestamp: now, userId: 'system' }]
    }
  ];

  salesInvoices.forEach(inv => {
    const invRef = doc(db, 'clients', clientId, 'gst_invoices', inv.id);
    batch.set(invRef, sanitizeForFirestore(inv), { merge: true });
  });

  // 4. Inward Purchase Invoices (Exact match, Missing in 2B, Blocked 17(5), RCM)
  const purchaseInvoices: ExtractedGstInvoice[] = [
    // Case 1: Exact Match in GSTR-2B
    {
      id: `demo_pur_1_${clientId}`,
      clientId,
      financialYear: currentFy,
      taxPeriod: currentPeriod,
      category: 'B2B_PURCHASES',
      supplier_gstin: '27AABCL4589F1Z2',
      supplier_name: 'Tata Communications Cloud Solutions',
      buyer_gstin: DEMO_GSTIN,
      invoice_number: 'TCL/2025/8812',
      normalized_invoice_number: 'TCL20258812',
      invoice_date: '2025-07-02',
      place_of_supply: '27-Maharashtra',
      reverse_charge_applicable: false,
      line_items: [
        {
          description: 'GPU Cloud Server Hosting & Datacenter Rack',
          hsn_sac_code: '998315',
          qty: 1,
          unit_price: 100000,
          taxable_value: 100000,
          cgst_rate: 9,
          sgst_rate: 9,
          igst_rate: 0,
          cess_rate: 0,
          cgst_amount: 9000,
          sgst_amount: 9000,
          igst_amount: 0,
          cess_amount: 0
        }
      ],
      total_taxable_value: 100000,
      total_tax: 18000,
      invoice_total: 118000,
      math_verified: true,
      itc_eligibility: 'ELIGIBLE',
      recon_status: 'EXACT_MATCH',
      source_file_path: 'tata_cloud_inv.pdf',
      created_at: now,
      audit_trail: [{ action: 'DEMO_SEEDED', timestamp: now, userId: 'system' }]
    },
    // Case 2: Missing in GSTR-2B (At Risk - Needs Vendor Nudge)
    {
      id: `demo_pur_2_${clientId}`,
      clientId,
      financialYear: currentFy,
      taxPeriod: currentPeriod,
      category: 'B2B_PURCHASES',
      supplier_gstin: '29AABCS9912K1Z9',
      supplier_name: 'Silicon Graphics Hardware Pvt Ltd',
      buyer_gstin: DEMO_GSTIN,
      invoice_number: 'SG/24-25/0942',
      normalized_invoice_number: 'SG2425942',
      invoice_date: '2025-07-15',
      place_of_supply: '27-Maharashtra',
      reverse_charge_applicable: false,
      line_items: [
        {
          description: 'Workstations & Dual Monitors for Engineering Lab',
          hsn_sac_code: '847130',
          qty: 2,
          unit_price: 75000,
          taxable_value: 150000,
          cgst_rate: 0,
          sgst_rate: 0,
          igst_rate: 18,
          cess_rate: 0,
          cgst_amount: 0,
          sgst_amount: 0,
          igst_amount: 27000,
          cess_amount: 0
        }
      ],
      total_taxable_value: 150000,
      total_tax: 27000,
      invoice_total: 177000,
      math_verified: true,
      itc_eligibility: 'ELIGIBLE',
      recon_status: 'MISSING_IN_2B',
      source_file_path: 'silicon_graphics_inv.pdf',
      created_at: now,
      audit_trail: [{ action: 'DEMO_SEEDED', timestamp: now, userId: 'system' }]
    },
    // Case 3: Blocked Credit under Section 17(5) (Food / Restaurant)
    {
      id: `demo_pur_3_${clientId}`,
      clientId,
      financialYear: currentFy,
      taxPeriod: currentPeriod,
      category: 'EXPENSE_RECEIPT',
      supplier_gstin: '27AAACG1122H1Z4',
      supplier_name: 'Taj Gateway Hospitality & Catering',
      buyer_gstin: DEMO_GSTIN,
      invoice_number: 'TAJ-BOM-4402',
      normalized_invoice_number: 'TAJBOM4402',
      invoice_date: '2025-07-20',
      place_of_supply: '27-Maharashtra',
      reverse_charge_applicable: false,
      line_items: [
        {
          description: 'Team Dinner and Annual Strategy Meeting Catering',
          hsn_sac_code: '996332',
          qty: 1,
          unit_price: 35000,
          taxable_value: 35000,
          cgst_rate: 2.5,
          sgst_rate: 2.5,
          igst_rate: 0,
          cess_rate: 0,
          cgst_amount: 875,
          sgst_amount: 875,
          igst_amount: 0,
          cess_amount: 0
        }
      ],
      total_taxable_value: 35000,
      total_tax: 1750,
      invoice_total: 36750,
      math_verified: true,
      itc_eligibility: 'BLOCKED_17_5',
      ai_compliance_note: 'Ineligible ITC under Section 17(5)(b)(i): Outdoor catering and restaurant services.',
      recon_status: 'UNMATCHED',
      source_file_path: 'taj_catering.pdf',
      created_at: now,
      audit_trail: [{ action: 'DEMO_SEEDED', timestamp: now, userId: 'system' }]
    },
    // Case 4: Reverse Charge (RCM) Legal Advocate Services
    {
      id: `demo_pur_4_${clientId}`,
      clientId,
      financialYear: currentFy,
      taxPeriod: currentPeriod,
      category: 'B2B_PURCHASES',
      supplier_gstin: '27AAALF9901B1Z3',
      supplier_name: 'Shardul Legal Associates LLP',
      buyer_gstin: DEMO_GSTIN,
      invoice_number: 'SLA/25/711',
      normalized_invoice_number: 'SLA25711',
      invoice_date: '2025-07-25',
      place_of_supply: '27-Maharashtra',
      reverse_charge_applicable: true,
      line_items: [
        {
          description: 'Statutory MCA Filing & Corporate Legal Retainer',
          hsn_sac_code: '998211',
          qty: 1,
          unit_price: 50000,
          taxable_value: 50000,
          cgst_rate: 9,
          sgst_rate: 9,
          igst_rate: 0,
          cess_rate: 0,
          cgst_amount: 4500,
          sgst_amount: 4500,
          igst_amount: 0,
          cess_amount: 0
        }
      ],
      total_taxable_value: 50000,
      total_tax: 9000,
      invoice_total: 59000,
      math_verified: true,
      itc_eligibility: 'RCM_APPLICABLE',
      ai_compliance_note: 'Section 9(3) Reverse Charge Mechanism: Recipient must pay tax in cash under Table 3.1(d) and claim ITC under Table 4(A)(3).',
      recon_status: 'EXACT_MATCH',
      source_file_path: 'legal_shardul.pdf',
      created_at: now,
      audit_trail: [{ action: 'DEMO_SEEDED', timestamp: now, userId: 'system' }]
    }
  ];

  purchaseInvoices.forEach(inv => {
    const invRef = doc(db, 'clients', clientId, 'gst_invoices', inv.id);
    batch.set(invRef, sanitizeForFirestore(inv), { merge: true });
  });

  // 5. GSTR-2B Portal Records (downloaded from GSTN)
  const gstr2bList: Gstr2bRecord[] = [
    {
      id: `demo_2b_1_${clientId}`,
      clientId,
      financialYear: currentFy,
      taxPeriod: currentPeriod,
      supplier_gstin: '27AABCL4589F1Z2',
      supplier_name: 'Tata Communications Cloud Solutions',
      invoice_number: 'TCL/2025/8812',
      normalized_invoice_number: 'TCL20258812',
      invoice_type: 'R',
      invoice_date: '2025-07-02',
      invoice_value: 118000,
      taxable_value: 100000,
      integrated_tax: 0,
      central_tax: 9000,
      state_tax: 9000,
      cess_amount: 0,
      itc_available: true,
      filing_date_by_supplier: '2025-08-10',
      created_at: now
    },
    {
      id: `demo_2b_2_${clientId}`,
      clientId,
      financialYear: currentFy,
      taxPeriod: currentPeriod,
      supplier_gstin: '27AAALF9901B1Z3',
      supplier_name: 'Shardul Legal Associates LLP',
      invoice_number: 'SLA/25/711',
      normalized_invoice_number: 'SLA25711',
      invoice_type: 'R',
      invoice_date: '2025-07-25',
      invoice_value: 59000,
      taxable_value: 50000,
      integrated_tax: 0,
      central_tax: 4500,
      state_tax: 4500,
      cess_amount: 0,
      itc_available: true,
      filing_date_by_supplier: '2025-08-11',
      created_at: now
    }
  ];

  gstr2bList.forEach(rec => {
    const recRef = doc(db, 'clients', clientId, 'gst_2b_records', rec.id);
    batch.set(recRef, sanitizeForFirestore(rec), { merge: true });
  });

  // 6. Merchant Invoices with Templates, E-Invoice & E-Way Bill
  const demoItems1 = [
    {
      id: 'item_1',
      description: 'Enterprise AI Agentic Workspace (10 Seats)',
      hsnSac: '998313',
      quantity: 1,
      unit: 'MONTHS',
      unitPrice: 85000,
      discountPercent: 10,
      discountAmount: 8500,
      taxableAmount: 76500,
      gstRate: 18,
      cgstAmount: 6885,
      sgstAmount: 6885,
      igstAmount: 0,
      cessAmount: 0,
      totalAmount: 90270
    },
    {
      id: 'item_2',
      description: 'Cloud Vector Database Managed Cluster',
      hsnSac: '998315',
      quantity: 2,
      unit: 'NOS',
      unitPrice: 20000,
      discountPercent: 0,
      discountAmount: 0,
      taxableAmount: 40000,
      gstRate: 18,
      cgstAmount: 3600,
      sgstAmount: 3600,
      igstAmount: 0,
      cessAmount: 0,
      totalAmount: 47200
    }
  ];

  const totals1 = calculateInvoiceTotals(demoItems1, '27', '27-Maharashtra');

  const merchantInvoice1: MerchantInvoice = {
    id: `demo_m_inv_1_${clientId}`,
    clientId,
    invoiceNumber: 'VERVE/25-26/104',
    invoiceDate: '2025-07-28',
    dueDate: '2025-08-12',
    invoiceType: 'B2B',
    financialYear: currentFy,
    taxPeriod: currentPeriod,
    sellerGstin: DEMO_GSTIN,
    sellerLegalName: DEMO_COMPANY,
    sellerTradeName: 'Verve AI Labs',
    sellerAddress: 'BKC, Bandra East, Mumbai - 400051',
    sellerPhone: '+91 98200 99881',
    sellerEmail: 'billing@verveailabs.com',
    sellerStateCode: '27',
    customerName: 'Finova Capital Technologies Pvt Ltd',
    customerGstin: '27AABCF9944M1ZQ',
    customerPhone: '9820123456',
    customerEmail: 'finance@finovacapital.in',
    customerBillingAddress: 'Cyber One, Vashi, Navi Mumbai, Maharashtra - 400703',
    placeOfSupply: '27-Maharashtra',
    isInterState: false,
    lineItems: totals1.items,
    subtotalTaxable: totals1.subtotalTaxable,
    totalCgst: totals1.totalCgst,
    totalSgst: totals1.totalSgst,
    totalIgst: totals1.totalIgst,
    totalCess: totals1.totalCess,
    totalTax: totals1.totalTax,
    roundOff: totals1.roundOff,
    grandTotal: totals1.grandTotal,
    status: 'ISSUED',
    paidAmount: 50000,
    balanceDue: totals1.grandTotal - 50000,
    paymentMode: 'UPI',
    templateId: 'CLASSIC_GST',
    notes: 'Thank you for choosing Verve AI Enterprise Suite.',
    termsAndConditions: settings.defaultTerms,
    eInvoice: {
      irn: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
      ackNo: '112519920112',
      ackDate: '2025-07-28 14:32:00',
      signedQrData: 'VERVE_EINVOICE_QR_DATA_DETERMINISTIC_MOCK',
      status: 'GENERATED',
      generatedAt: now
    },
    eWayBill: {
      ewbNumber: '341098234567',
      ewbDate: '2025-07-28 14:35:00',
      validUpto: '2025-07-30 23:59:00',
      transporterId: '27AAACG0500P1ZT',
      transporterName: 'Verve Logistics Express',
      vehicleNumber: 'MH-04-AX-8911',
      distanceKm: 85,
      supplyType: 'OUTWARD',
      subSupplyType: 'SUPPLY',
      docType: 'INV',
      status: 'GENERATED',
      generatedAt: now
    },
    createdAt: now,
    updatedAt: now
  };

  const mInvRef1 = doc(db, 'clients', clientId, 'merchant_invoices', merchantInvoice1.id);
  batch.set(mInvRef1, sanitizeForFirestore(merchantInvoice1), { merge: true });

  await batch.commit();

  return {
    salesCount: salesInvoices.length,
    purchaseCount: purchaseInvoices.length,
    gstr2bCount: gstr2bList.length,
    billingInvoiceCount: 1
  };
}
